import React, { ChangeEvent, FocusEvent, useContext } from 'react';

import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';

import PasswordInput from 'components/common/PasswordInput/PasswordInput';
import PasswordValidationPoints from 'components/common/PasswordValidationPoints/PasswordValidationPoints';
import { matchConfirmedPassword, validatePassword, validateRequiredField } from 'common/utils/validation.utils';
import Button from 'components/common/Button/Button';
import useForm, { FieldValues } from 'hooks/useForm';
import { RootStoreContext } from 'index';
import { notify } from 'components/common/Toast/toast.helpers';
import { ToastMessage } from 'components/common/Toast/toast.enums';
import { UpdatePasswordErrorMessage } from 'components/auth/auth.dto';
import { ApplicationRoute } from 'routes/routes.enums';

import styles from './UpdatePassword.module.scss';
import { UpdatePasswordFieldName } from './updatePassword.enums';

const UpdatePassword = ({ onClose }: { onClose: () => void }) => {
  const { authStore, intermediaryApplicationsStore } = useContext(RootStoreContext);
  const {
    authStore: { isLoading },
  } = useContext(RootStoreContext);
  const navigator = useNavigate();
  const { errors, values, setValue, validateField, handleSubmit, setErrors } = useForm<FieldValues>({
    defaultValues: {
      [UpdatePasswordFieldName.OldPassword]: '',
      [UpdatePasswordFieldName.NewPassword]: '',
      [UpdatePasswordFieldName.ConfirmNewPassword]: '',
    },
    handleValidation: (fieldName, value) => {
      switch (fieldName) {
        case UpdatePasswordFieldName.OldPassword: {
          return validateRequiredField(value, fieldName);
        }
        case UpdatePasswordFieldName.NewPassword: {
          return (
            validateRequiredField(value, fieldName) || validatePassword(values[UpdatePasswordFieldName.NewPassword])
          );
        }
        case UpdatePasswordFieldName.ConfirmNewPassword: {
          return (
            validateRequiredField(value, fieldName) ||
            matchConfirmedPassword(
              values[UpdatePasswordFieldName.NewPassword],
              values[UpdatePasswordFieldName.ConfirmNewPassword],
            )
          );
        }
        default:
          return '';
      }
    },
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setValue(name, value);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;

    validateField(name, values[name]);
  };

  const handleResetPassword = handleSubmit(async () => {
    const response = await authStore.updatePassword(
      values[UpdatePasswordFieldName.NewPassword],
      values[UpdatePasswordFieldName.OldPassword],
    );

    if (response.success) {
      notify(ToastMessage.PasswordChangedSuccessfully, 'success');
      onClose();
      await authStore.logout();
      await intermediaryApplicationsStore.clearStore();
      navigator(ApplicationRoute.Login);
    } else {
      if (response.message === UpdatePasswordErrorMessage.InvalidPassword) {
        setErrors({
          [UpdatePasswordFieldName.OldPassword]: 'Invalid Password. Please check your Password and try again',
        });
      } else {
        notify(ToastMessage.DefaultError, 'error');
      }
    }
  });

  return (
    <>
      <PasswordInput
        labelTitle={UpdatePasswordFieldName.OldPassword}
        name={UpdatePasswordFieldName.OldPassword}
        tabIndex={2}
        value={values[UpdatePasswordFieldName.OldPassword]}
        containerClassName={styles.passwordInputContainer}
        errorMessage={errors[UpdatePasswordFieldName.OldPassword]}
        onChange={handleChange}
        onBlur={handleBlur}
        required
      />
      <PasswordInput
        labelTitle={UpdatePasswordFieldName.NewPassword}
        name={UpdatePasswordFieldName.NewPassword}
        tabIndex={2}
        value={values[UpdatePasswordFieldName.NewPassword]}
        onBlur={handleBlur}
        containerClassName={styles.passwordInputContainer}
        errorMessage={errors[UpdatePasswordFieldName.NewPassword]}
        onChange={handleChange}
        required
      />
      <PasswordValidationPoints password={values[UpdatePasswordFieldName.NewPassword]} />
      <PasswordInput
        labelTitle={UpdatePasswordFieldName.ConfirmNewPassword}
        name={UpdatePasswordFieldName.ConfirmNewPassword}
        tabIndex={2}
        value={values[UpdatePasswordFieldName.ConfirmNewPassword]}
        containerClassName={styles.passwordInputContainer}
        errorMessage={errors[UpdatePasswordFieldName.ConfirmNewPassword]}
        onChange={handleChange}
        onBlur={handleBlur}
        required
      />
      <Button className={styles.sendButton} onClick={handleResetPassword} isLoading={isLoading}>
        Submit
      </Button>
    </>
  );
};

export default observer(UpdatePassword);
